import { useServicesContext } from "../../providers/ServicesProvider";
import { IAccessContext } from "../../services/UserService";
import useSWR from "swr";
import { UserGroup } from "../../domain/UserGroup";
import { Collection } from "../../domain/Collection";
import { useProducts } from "../useProducts/useProducts";
import { useAttributes } from "../useAttributes/useAttributes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OrganizationPermission, ProductPermission } from "../../domain/Permission.interface";
import {
    filterPermissionListsForContext,
    getOrgPermissionsFromGroupList,
    getProjects,
    orgPermissionListToAccessList
} from "../../services/AccessListTransforms";
import { SWR_KEY_PREFIX } from "../../constants/constants";
import { datadogRum } from "@datadog/browser-rum";
import { ERROR_KEYS } from "../../constants/data-dog-constants";

export const useAccessList = ({
    organizationId,
    instanceIds,
    context
}: {
    organizationId?: string;
    instanceIds?: string[];
    context?: IAccessContext;
}): {
    accessList: ProductPermission[] | undefined;
    error: Error | undefined;
    isLoading: boolean | undefined;
} => {
    const { userService } = useServicesContext();
    const { products, isLoading: productsLoading } = useProducts({ organizationId, includeServices: true });
    const { getExperimentationProjects } = useAttributes({ isActive: true });

    const [sortedInstanceIdsString, setSortedInstanceIdsString] = useState("");

    // Update the sorted string only when the value actually changes
    useEffect(() => {
        const currentSortedString = instanceIds?.length ? [...instanceIds].sort().join(",") : "";

        if (currentSortedString !== sortedInstanceIdsString) {
            setSortedInstanceIdsString(currentSortedString);
        }
    }, [instanceIds, sortedInstanceIdsString]);

    const sortedInstanceIds = useMemo(
        () => (sortedInstanceIdsString ? sortedInstanceIdsString.split(",") : []),
        [sortedInstanceIdsString]
    );

    const userGroupsKey = useMemo(() => {
        if (!organizationId || !sortedInstanceIdsString) return null;
        return `${SWR_KEY_PREFIX.GROUPS}/api/usergroups/?organizationId=${organizationId}&instanceIds=${sortedInstanceIdsString}`;
    }, [organizationId, sortedInstanceIdsString]);

    // We ran into an issue in the Solution Architect's org where there were too many permissions which caused the API
    // to fail. Here, we split the instanceIds into chunks to avoid hitting URL length limits and also paginate the
    // response to avoid overloading the API with too many results at once. PLM will be investigating limitations
    // and optimal query parameters in the future.
    const fetchUserGroups = useCallback(async (): Promise<Collection<UserGroup>> => {
        if (!organizationId || !sortedInstanceIds?.length) {
            return Promise.resolve({ items: [], totalCount: 0 });
        }

        const chunkSize = 40;
        const chunks = sortedInstanceIds.reduce((resultArray, item, index) => {
            const chunkIndex = Math.floor(index / chunkSize);
            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = [];
            }
            resultArray[chunkIndex].push(item);
            return resultArray;
        }, [] as string[][]);

        const pageSize = 1000;
        const fetchAllPages = async (chunk: string[]): Promise<Collection<UserGroup>> => {
            let allItems: UserGroup[] = [];
            let totalCount = 0;
            let page = 1;

            do {
                const result = await userService!.getUserGroups({
                    organizationId,
                    instanceIds: chunk,
                    size: pageSize,
                    page: page++
                });
                allItems = [...allItems, ...result.items];
                totalCount = result.totalCount;
            } while (allItems.length < totalCount);

            return { items: allItems, totalCount };
        };

        const promises = chunks.map((chunk) => fetchAllPages(chunk));
        const results = await Promise.all(promises);

        return results.reduce(
            (acc, curr) => ({
                items: [...acc.items, ...curr.items],
                totalCount: acc.totalCount + curr.totalCount
            }),
            { items: [], totalCount: 0 } as Collection<UserGroup>
        );
    }, [organizationId, sortedInstanceIds, userService]);

    const { data: userGroupsData, error: userGroupsError } = useSWR<Collection<UserGroup>, Error>(
        userGroupsKey,
        fetchUserGroups
    );

    const userGroupsLoading = !userGroupsData && !userGroupsError && !!userGroupsKey;

    const processAccessList = useCallback(async (): Promise<ProductPermission[] | undefined> => {
        if (!userGroupsData || !products || userGroupsError) {
            return undefined;
        }

        try {
            let orgPermissions: OrganizationPermission[] | undefined = getOrgPermissionsFromGroupList(
                userGroupsData.items,
                products
            );

            await Promise.all(getProjects(orgPermissions, getExperimentationProjects));

            if (context) {
                orgPermissions = filterPermissionListsForContext(context, orgPermissions);
            }

            return orgPermissionListToAccessList(orgPermissions);
        } catch (error) {
            datadogRum.addError(new Error(ERROR_KEYS.ACCESS_LIST.PROCESS_FAILED), {
                originalError: error,
                userGroupCount: userGroupsData?.items.length,
                productCount: products?.length,
                hasContext: !!context,
                source: "useAccessList.processAccessList"
            });
            return undefined;
        }
    }, [userGroupsData, products, userGroupsError, getExperimentationProjects, context]);

    const accessListKey = useMemo(() => {
        if (userGroupsError || !userGroupsData || !products) return null;
        const contextKey = context
            ? `${context.scope}:${context.items
                  .map((item) => item.id)
                  .sort()
                  .join(",")}`
            : "";

        return `access-list/processed/?groups=${userGroupsData.items.length}&products=${products.length}&instances=${sortedInstanceIdsString}&context=${contextKey}`;
    }, [userGroupsData, userGroupsError, products, context, sortedInstanceIdsString]);

    const { data: processedAccessList, error: processError } = useSWR(accessListKey, processAccessList);

    const accessListLoading = !processedAccessList && !processError && !!accessListKey;

    const isLoading = productsLoading || userGroupsLoading || accessListLoading;

    return {
        accessList: processedAccessList,
        isLoading,
        error: userGroupsError || processError
    };
};
